import { useState } from "react"
import { Link } from "@inertiajs/react"
import { X, Bell } from "lucide-react"
import { Button } from "@/components/ui/button"

const CADENCE_LABEL = {
  monthly: "monthly",
  quarterly: "quarterly",
  yearly: "yearly",
}

// Soft 'time to take your wheel again' banner. Only renders when the
// CadenceReminder service judged the member due. Dismissable for the
// session via sessionStorage; coach cadence config and the latest
// WheelSnapshot drive when it returns.
//
// Props:
//   reminder: { cadence, latestTakenAt, dueDays } | null
export function AssessmentReminderBanner({ reminder }) {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false
    try {
      return sessionStorage.getItem("kf:assessment-reminder-dismissed") === "1"
    } catch {
      return false
    }
  })

  if (!reminder || dismissed) return null

  const cadenceLabel = CADENCE_LABEL[reminder.cadence] || reminder.cadence
  const days = reminder.dueDays
  const lastTaken = reminder.latestTakenAt
    ? new Date(reminder.latestTakenAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null

  const handleDismiss = () => {
    setDismissed(true)
    try {
      sessionStorage.setItem("kf:assessment-reminder-dismissed", "1")
    } catch {
      // Storage disabled (e.g., private mode); soft-state is fine.
    }
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <div className="flex items-start gap-2 min-w-0">
        <Bell className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" aria-hidden />
        <div className="text-sm text-amber-900">
          <p className="font-medium">
            Time for a {cadenceLabel} reassessment.
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            {lastTaken
              ? `Last taken ${lastTaken} (~${days} days ago).`
              : "You haven't taken a wheel yet."}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link href="/app/member/balance_wheel/retake">
          <Button type="button" variant="kf-outline" size="sm">
            Take wheel again
          </Button>
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss reminder"
          className="p-1 text-amber-700 hover:text-amber-900"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
